// ==============================
// File: src/routes/guards.jsx
// Route guards used in <Routes>
// - RequireAuth: gate pages until Supabase user (or any user object) exists
// - RequireSelectedProduct: ensure a product is chosen (eg AdvancedCalculator)
// - RequirePlan: gate by plan (array/Set of allowed, or a predicate)
// - RequireFeatures: gate by feature keys using usePlan() matrix
//
// All guards preserve 'returnTo' so we can bounce back post-action.
// ==============================
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import usePlan from "@/hooks/usePlan";

/* ------------------------------------------
 * helpers
 * ----------------------------------------*/
function withReturnTo(to, location) {
  try {
    const url = new URL(to, window.location.origin);
    const already = url.searchParams.get("returnTo");
    if (!already) {
      const here = location?.pathname + (location?.search || "") + (location?.hash || "");
      url.searchParams.set("returnTo", here || "/");
    }
    return url.pathname + url.search + url.hash;
  } catch {
    // if URL constructor fails (relative), append safely
    const here = encodeURIComponent(
      (location?.pathname || "/") + (location?.search || "") + (location?.hash || "")
    );
    const sep = String(to).includes("?") ? "&" : "?";
    return `${to}${sep}returnTo=${here}`;
  }
}

/* ------------------------------------------
 * RequireAuth
 * - Expects a user object (eg from AuthProvider / Supabase)
 * - If missing, sends to /login and preserves returnTo
 * ----------------------------------------*/
export function RequireAuth({ user, children, loginPath = "/login" }) {
  const location = useLocation();
  if (!user) {
    console.log("[AuthGuard] redirecting to login (routes/guards)", {
      path: location?.pathname,
      user: user || null,
    });
    const dest = withReturnTo(loginPath, location);
    return <Navigate to={dest} replace />;
  }
  console.log("[AuthGuard] allow (routes/guards)", {
    path: location?.pathname,
    user: user?.id,
  });
  return children;
}

/* ------------------------------------------
 * RequireSelectedProduct
 * - Ensure a product is chosen (for routes that depend on it)
 * - Redirects to /gallery by default, preserving returnTo
 * ----------------------------------------*/
export function RequireSelectedProduct({
  selectedProduct,
  children,
  fallback = "/gallery",
}) {
  const location = useLocation();
  if (!selectedProduct) {
    const dest = withReturnTo(fallback, location);
    return <Navigate to={dest} replace />;
  }
  return children;
}

/* ------------------------------------------
 * RequirePlan
 * - currentPlan: string like "free" | "pro" | "team"
 * - allow: array or Set of allowed plan strings, OR a predicate(plan)=>bool
 * - fallback defaults to /settings
 * ----------------------------------------*/
export function RequirePlan({
  currentPlan,
  allow = ["pro", "team"],
  children,
  fallback = "/settings",
}) {
  const location = useLocation();

  let ok = false;
  if (typeof allow === "function") {
    ok = !!allow(currentPlan);
  } else if (allow instanceof Set) {
    ok = allow.has(currentPlan);
  } else if (Array.isArray(allow)) {
    ok = allow.includes(currentPlan);
  } else {
    // single string
    ok = currentPlan === allow;
  }

  if (!ok) {
    const dest = withReturnTo(fallback, location);
    return <Navigate to={dest} replace />;
  }
  return children;
}

/* ------------------------------------------
 * RequireFeatures
 * - For finer control using your usePlan() feature matrix.
 * - `needs` can be a single feature or an array of features that must all be present.
 * - Example features (from usePlan): "core", "planner", "gallery", "packaging",
 *   "analytics", "workshops", "staffTargets"
 * ----------------------------------------*/
export function RequireFeatures({
  plan = "free",
  needs = [],
  children,
  fallback = "/settings",
}) {
  const location = useLocation();
  const { canUse } = usePlan(plan);

  const required = Array.isArray(needs) ? needs : [needs];
  const ok = required.every((f) => canUse(f));

  if (!ok) {
    const dest = withReturnTo(fallback, location);
    return <Navigate to={dest} replace />;
  }
  return children;
}
